import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { SEOHead } from '../components';
import {
  InDocHeroDiagram,
  InDocInvestigationDiagram,
  InDocScopeDiagram,
} from '../components/executive';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Building2,
  FileSearch,
  XCircle,
  ShieldCheck,
  Search,
  Server,
  ScrollText,
} from 'lucide-react';

const residual = [
  {
    icon: Search,
    title: 'Both Indexes Vote',
    detail:
      'Keyword and meaning, scored together. The hit feeds the next move. The hit is not the deliverable.',
  },
  {
    icon: Server,
    title: 'Models On The Iron',
    detail:
      'Local inference is the working path. A public API is a fallback switch, not the architecture.',
  },
  {
    icon: ShieldCheck,
    title: 'Clean Before Queryable',
    detail:
      'Scan, extract, embed, write both indexes. Until that finishes, the file is not in play.',
  },
  {
    icon: ScrollText,
    title: 'The Path Stays',
    detail:
      'Thought, tool, result, citation — kept. An answer with no path does not ship.',
  },
];

const riskConcerns = [
  'The brief names documents. Floating prose does not leave the room',
  'The agent cannot reach what the role cannot reach. That is code',
  'Counsel can open the run and see how the number was found',
];

const deskConcerns = [
  'State the objective. Watch the loop. Take the brief',
  'Index the library once. Query it in place',
  'Out of scope holds. It does not leak',
];

const sessionItems = [
  'A live run against a real library, or a stand-in',
  'A file the role cannot see — and the agent missing it',
  'The ledger after the brief lands',
  'Where the model sits, and what never leaves the room',
];

const notClaiming = [
  'Counsel still owns the legal call. The records officer still owns the file.',
  'Models miss. The ledger is how that gets found.',
  'The working inference path is local. Cloud is a switch, not the default.',
];

const verticals = [
  'Legal',
  'Healthcare',
  'Finance',
  'Insurance',
  'Records',
  'Diligence',
];

const InDoc = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const eyebrow = 'text-xs uppercase tracking-[0.35em] text-slate-400';
  const bodyStrong = isDark ? 'text-slate-300' : 'text-slate-700';
  const bodyMuted = isDark ? 'text-slate-400' : 'text-slate-600';
  const cardShell = isDark
    ? 'bg-white/[0.03] border border-white/10'
    : 'bg-white border border-slate-200 shadow-sm';

  return (
    <div
      className={
        isDark ? 'bg-[#070b14] text-slate-100' : 'bg-white text-slate-800'
      }
    >
      <SEOHead
        title="inDoc — The Answer Arrives With The Path"
        description="An agent works the document library — search, read, compare — inside the signed-in role's access line. The brief cites sources. The run can be replayed. Models can stay on-premise."
        type="website"
      />

      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: isDark
              ? 'radial-gradient(ellipse 80% 55% at 90% 0%, rgba(56,189,248,0.18), transparent 50%), radial-gradient(ellipse 60% 45% at 5% 95%, rgba(99,102,241,0.16), transparent 48%)'
              : 'radial-gradient(ellipse 80% 55% at 90% 0%, rgba(56,189,248,0.10), transparent 50%), radial-gradient(ellipse 60% 45% at 5% 95%, rgba(99,102,241,0.08), transparent 48%)',
          }}
        />
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20">
          <Link
            to="/"
            className={`inline-flex items-center gap-2 text-sm font-medium mb-8 ${isDark ? 'text-sky-400 hover:text-sky-300' : 'text-sky-700 hover:text-sky-900'}`}
          >
            <ArrowLeft size={16} aria-hidden />
            Shared Oxygen
          </Link>

          <div className="grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-10 lg:gap-14 items-center">
            <div className="space-y-5 max-w-xl">
              <h1 className="text-4xl lg:text-5xl font-bold tracking-tight">
                inDoc
              </h1>
              <p className={`text-sm font-medium ${bodyMuted}`}>
                Investigation Over Restricted Libraries
              </p>

              <h2 className="text-3xl lg:text-[2.35rem] font-bold leading-[1.14] tracking-tight">
                The Closed Drawer Never Opened. The Open Files Have Citations.
              </h2>

              <p className={`text-lg leading-relaxed ${bodyStrong}`}>
                inDoc puts an agent on the library. The model chooses the next
                move — search, read, compare — and stays in the loop until the
                brief can name its sources, or the steps run out. Tools only
                open files the signed-in role is allowed to see.
              </p>

              <p className={`text-base leading-relaxed ${bodyMuted}`}>
                The run is still there after the answer. The model can stay on
                the operator&apos;s machines.
              </p>

              <div className="flex flex-wrap gap-4 pt-1">
                <Link
                  to="/contact?subject=inDoc%20Live%20Run"
                  className="group inline-flex items-center gap-2 px-8 py-4 bg-sky-500 hover:bg-sky-600 text-slate-900 font-bold rounded-xl transition-colors"
                >
                  Request A Live Run
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact?subject=inDoc%20Architecture%20Review"
                  className={`inline-flex items-center gap-2 px-8 py-4 font-semibold rounded-xl border transition-colors ${
                    isDark
                      ? 'border-slate-600 text-slate-200 hover:border-sky-500 hover:text-sky-300'
                      : 'border-slate-300 text-slate-700 hover:border-sky-600 hover:text-sky-800'
                  }`}
                >
                  Architecture Review
                </Link>
              </div>
            </div>

            <div className="w-full min-w-0">
              <InDocHeroDiagram theme={theme} />
            </div>
          </div>
        </div>
      </section>

      <section
        className={`py-14 lg:py-20 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}
      >
        <div className="max-w-6xl mx-auto px-6">
          <p className={`${eyebrow} mb-3`}>The Loop</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">
            Search Hands Back Snippets. The Agent Keeps Working.
          </h3>
          <p className={`max-w-2xl mb-10 ${bodyMuted}`}>
            A wrong first hit gets a different search. A promising file gets
            read. Two versions get compared. The run ends when the brief can
            stand, or when the budget is gone. Either way the path is sitting
            there.
          </p>
          <InDocInvestigationDiagram theme={theme} />
        </div>
      </section>

      <section
        className={`py-14 lg:py-20 ${isDark ? 'bg-slate-950/50' : 'bg-sky-50/40'}`}
      >
        <div className="max-w-6xl mx-auto px-6">
          <p className={`${eyebrow} mb-3`}>The Line</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">
            Access Is Enforced In The Tool. Not In The Prompt.
          </h3>
          <p className={`max-w-2xl mb-10 ${bodyMuted}`}>
            Search, read, summarize, and compare only resolve files the
            signed-in role may touch. A closed contract does not appear in
            results, does not get summarized, and cannot be cited. A prompt
            cannot talk the agent past that.
          </p>
          <InDocScopeDiagram theme={theme} />
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <p className={`${eyebrow} mb-3`}>After The Answer</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-8">
            The Brief Leaves. The Path Does Not.
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {residual.map(({ icon: Icon, title, detail }) => (
              <div key={title} className={`p-6 rounded-xl ${cardShell}`}>
                <Icon className="w-6 h-6 mb-3 text-sky-400" aria-hidden />
                <h4 className="font-bold mb-2">{title}</h4>
                <p className={`text-sm leading-relaxed ${bodyMuted}`}>
                  {detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className={`py-14 lg:py-20 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}
      >
        <div className="max-w-6xl mx-auto px-6">
          <p className={`${eyebrow} mb-3`}>Who Lives With This</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-10">
            The Brief Has To Survive Counsel And The Desk.
          </h3>
          <div className="grid lg:grid-cols-2 gap-6">
            <div
              className={`p-7 rounded-2xl border-l-4 border-l-indigo-500 ${cardShell}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <Building2 className="w-5 h-5 text-indigo-400" aria-hidden />
                <h4 className="font-bold text-lg">For The Risk Owner</h4>
              </div>
              <ul className="space-y-3">
                {riskConcerns.map(item => (
                  <li key={item} className="flex gap-3">
                    <CheckCircle2
                      className={`w-4 h-4 flex-shrink-0 mt-1 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}
                      aria-hidden
                    />
                    <span className={`text-sm leading-relaxed ${bodyStrong}`}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className={`p-7 rounded-2xl border-l-4 border-l-sky-500 ${cardShell}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <FileSearch className="w-5 h-5 text-sky-400" aria-hidden />
                <h4 className="font-bold text-lg">For The Operating Desk</h4>
              </div>
              <ul className="space-y-3">
                {deskConcerns.map(item => (
                  <li key={item} className="flex gap-3">
                    <CheckCircle2
                      className={`w-4 h-4 flex-shrink-0 mt-1 ${isDark ? 'text-sky-400' : 'text-sky-700'}`}
                      aria-hidden
                    />
                    <span className={`text-sm leading-relaxed ${bodyStrong}`}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-8">
            {verticals.map(v => (
              <span
                key={v}
                className={`text-sm font-medium px-4 py-1.5 rounded-full border ${
                  isDark
                    ? 'border-white/10 text-slate-400 bg-white/[0.03]'
                    : 'border-slate-200 text-slate-600 bg-white'
                }`}
              >
                {v}
              </span>
            ))}
          </div>

          <div
            className={`mt-8 p-7 rounded-2xl border ${isDark ? 'bg-slate-900/60 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}
          >
            <p className={`${eyebrow} mb-4`}>Limits</p>
            <div className="grid sm:grid-cols-3 gap-5">
              {notClaiming.map(item => (
                <div key={item} className="flex gap-3">
                  <XCircle
                    className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                    aria-hidden
                  />
                  <span className={`text-sm leading-relaxed ${bodyStrong}`}>
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className={`py-14 lg:py-20 ${isDark ? 'bg-slate-950/80' : 'bg-slate-50'}`}
      >
        <div className="max-w-4xl mx-auto px-6">
          <p className={`${eyebrow} mb-3`}>On The Screen</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-8">
            What Has To Happen In The Room
          </h3>
          <div className="grid sm:grid-cols-2 gap-5">
            {sessionItems.map(item => (
              <div key={item} className={`p-5 rounded-xl ${cardShell}`}>
                <p className={`text-sm leading-relaxed ${bodyStrong}`}>
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className={`py-16 lg:py-20 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}
      >
        <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
          <h3
            className={`text-3xl lg:text-4xl font-bold leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            Bring One Library And One Hard Question
          </h3>
          <p className={`text-lg leading-relaxed ${bodyMuted}`}>
            The session covers the files, who can see them, and where the model
            is allowed to run. A mismatch gets named in the room.
          </p>
          <Link
            to="/contact?subject=inDoc%20Conversation"
            className="inline-flex items-center gap-2 px-10 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors"
          >
            Request A Live Run
            <ArrowRight className="w-5 h-5" aria-hidden />
          </Link>
          <p
            className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          >
            inDoc is a product of Shared Oxygen, LLC
          </p>
        </div>
      </section>
    </div>
  );
};

export default InDoc;
